import { useRef, useCallback } from 'react'
import { toast } from 'react-toastify'
import { isAllowedAttachmentType, formatFileSize } from '../constants/fileTypes'
import { Attachment } from '../components/Attachments/AttachmentList'

interface UseFileAttachmentsOptions {
  allowedTypes: readonly string[]
  maxFileSize: number
  maxTotalSize?: number
  onAdd?: (newAttachments: Attachment[]) => void
}

export const useFileAttachments = (options: UseFileAttachmentsOptions) => {
  const {
    allowedTypes,
    maxFileSize,
    maxTotalSize = maxFileSize,
    onAdd,
  } = options

  const fileInputRef = useRef<HTMLInputElement>(null)

  const openFileSelect = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const validateAndAddFiles = useCallback(
    (
      files: FileList | null,
      currentAttachments: Attachment[] = []
    ): Attachment[] | null => {
      if (!files) return null

      const filesArr = Array.from(files)

      // Validate file types
      if (
        filesArr.some(
          (file) => !isAllowedAttachmentType(file.name, allowedTypes)
        )
      ) {
        toast.error(
          `Only the following file types are allowed: ${allowedTypes.join(', ')}`
        )
        return null
      }

      // Validate individual file sizes
      if (filesArr.some((file) => file.size > maxFileSize)) {
        toast.error(
          `The maximum file size allowed is ${formatFileSize(maxFileSize)}`
        )
        return null
      }

      // Validate total size
      const currentTotalSize = currentAttachments.reduce(
        (sum, att) => sum + att.size,
        0
      )
      const newTotalSize =
        currentTotalSize + filesArr.reduce((sum, file) => sum + file.size, 0)

      if (newTotalSize > maxTotalSize) {
        toast.error(
          `Total attachments size would exceed ${formatFileSize(maxTotalSize)} limit`
        )
        return null
      }

      // Create attachment objects
      const newAttachments: Attachment[] = filesArr.map((file) => ({
        file,
        filename: file.name,
        size: file.size,
      }))

      toast.success(`${filesArr.length} file(s) attached`)
      return newAttachments
    },
    [allowedTypes, maxFileSize, maxTotalSize]
  )

  const handleFileSelect = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      currentAttachments: Attachment[] = []
    ) => {
      e.preventDefault()
      const files = e.target.files
      const newAttachments = validateAndAddFiles(files, currentAttachments)

      if (newAttachments) {
        onAdd?.(newAttachments)
      }

      // Reset input to allow re-selecting the same file
      e.target.value = ''
    },
    [validateAndAddFiles, onAdd]
  )

  const createRemoveHandler = useCallback(
    (onRemove: (index: number) => void) => (index: number) => {
      onRemove(index)
    },
    []
  )

  return {
    fileInputRef,
    openFileSelect,
    handleFileSelect,
    validateAndAddFiles,
    createRemoveHandler,
  }
}
